import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Rocket, X, Loader2 } from "lucide-react";

const defaultPeriod = () => {
    const d = new Date();
    const q = Math.floor(d.getMonth() / 3) + 1;
    return `${d.getFullYear()}-Q${q}`;
};

export default function PerformanceCycleDialog({ open, onClose, onLaunched }) {
    const [period, setPeriod] = useState(defaultPeriod());
    const [department, setDepartment] = useState("");
    const [departments, setDepartments] = useState([]);
    const [preview, setPreview] = useState(null);
    const [previewing, setPreviewing] = useState(false);
    const [launching, setLaunching] = useState(false);

    useEffect(() => {
        if (!open) return;
        api.get("/departments").then(({ data }) => setDepartments(data || [])).catch(() => {});
    }, [open]);

    useEffect(() => {
        if (!open || !period.trim()) { setPreview(null); return; }
        let alive = true;
        setPreviewing(true);
        const params = new URLSearchParams({ period: period.trim() });
        if (department) params.append("department", department);
        api.get(`/performance/cycle-preview?${params.toString()}`)
            .then(({ data }) => { if (alive) setPreview(data); })
            .catch((e) => { if (alive) { setPreview(null); toast.error(formatApiError(e)); } })
            .finally(() => { if (alive) setPreviewing(false); });
        return () => { alive = false; };
    }, [open, period, department]);

    const launch = async () => {
        if (!period.trim()) { toast.error("Period is required."); return; }
        setLaunching(true);
        try {
            const { data } = await api.post("/performance/bulk-cycle", {
                period: period.trim(),
                department: department || null,
            });
            toast.success(`Launched cycle ${period} — created ${data.created} draft${data.created === 1 ? "" : "s"}, skipped ${data.skipped}.`);
            onLaunched?.(data);
            onClose?.();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLaunching(false);
        }
    };

    if (!open) return null;
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 p-4" data-testid="perf-cycle-dialog">
            <div className="bg-white w-full max-w-lg border border-ink/10 shadow-xl">
                <div className="flex items-center justify-between p-5 border-b border-ink/10">
                    <div>
                        <div className="overline">Performance</div>
                        <h3 className="mt-1 font-display font-bold text-xl tracking-tight text-ink flex items-center gap-2">
                            <Rocket className="w-5 h-5 text-klein" /> Launch a performance cycle
                        </h3>
                    </div>
                    <button onClick={onClose} data-testid="perf-cycle-close" className="text-ink/60 hover:text-ink"><X className="w-5 h-5" /></button>
                </div>

                <div className="p-5 space-y-4">
                    <div>
                        <label className="overline">Period</label>
                        <input
                            type="text"
                            value={period}
                            onChange={(e) => setPeriod(e.target.value)}
                            placeholder="e.g. 2026-Q1, 2026-H1, 2026"
                            data-testid="perf-cycle-period"
                            className="mt-1 w-full border border-ink/15 px-3 py-2 text-sm"
                        />
                        <p className="mt-1 text-xs text-ink/50">A draft self-review is created per active employee for this period.</p>
                    </div>
                    <div>
                        <label className="overline">Department (optional)</label>
                        <select
                            value={department}
                            onChange={(e) => setDepartment(e.target.value)}
                            data-testid="perf-cycle-department"
                            className="mt-1 w-full border border-ink/15 px-3 py-2 text-sm bg-white"
                        >
                            <option value="">All departments</option>
                            {departments.map((d) => (
                                <option key={d.id} value={d.name}>{d.name}</option>
                            ))}
                        </select>
                    </div>

                    <div className="grid grid-cols-3 gap-px bg-ink/10 border border-ink/10" data-testid="perf-cycle-preview">
                        <Stat label="In scope" value={previewing ? "…" : (preview?.scope_size ?? 0)} testid="perf-cycle-stat-scope" />
                        <Stat label="Will create" value={previewing ? "…" : (preview?.would_create ?? 0)} accent testid="perf-cycle-stat-create" />
                        <Stat label="Already exist" value={previewing ? "…" : (preview?.would_skip ?? 0)} testid="perf-cycle-stat-skip" />
                    </div>
                    {preview?.skipped_names?.length > 0 && (
                        <div className="text-xs text-ink/50">
                            Skipping (already have a draft/submission): {preview.skipped_names.join(", ")}{preview.would_skip > preview.skipped_names.length ? ` +${preview.would_skip - preview.skipped_names.length} more` : ""}
                        </div>
                    )}
                </div>

                <div className="flex items-center justify-end gap-3 p-5 border-t border-ink/10 bg-ink/[0.02]">
                    <button onClick={onClose} disabled={launching} className="text-sm text-ink/60 hover:text-ink px-4 py-2">Cancel</button>
                    <button
                        onClick={launch}
                        disabled={launching || !preview || preview.would_create === 0}
                        data-testid="perf-cycle-launch-btn"
                        className="inline-flex items-center gap-2 bg-klein text-white px-4 py-2 text-sm hover:bg-klein/90 disabled:opacity-50"
                    >
                        {launching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Rocket className="w-4 h-4" />}
                        {launching ? "Launching…" : `Launch — create ${preview?.would_create ?? 0} drafts`}
                    </button>
                </div>
            </div>
        </div>
    );
}

function Stat({ label, value, accent, testid }) {
    return (
        <div className={`p-4 ${accent ? "bg-klein text-white" : "bg-white"}`} data-testid={testid}>
            <div className={`overline ${accent ? "!text-white/70" : ""}`}>{label}</div>
            <div className="mt-2 font-display font-black text-2xl tabular-nums">{value}</div>
        </div>
    );
}
